import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import IconButton from './IconButton';
import styles from './Modal.module.css';

export type ModalSize = 'sm' | 'md' | 'lg';

export interface ModalProps {
  open: boolean;
  /** Not called while `dismissible` is false, since nothing can then close it. */
  onClose?: () => void;
  title: string;
  /** A sentence under the title. The place for the consequence of the action,
      which is what someone actually needs before confirming. */
  description?: string;
  children?: ReactNode;
  /** Buttons for the footer. Put the primary action last: on every platform the
      confirming action sits on the right of a dialog, and reversing it means
      people click the wrong one from muscle memory. */
  footer?: ReactNode;
  /**
   * How much room the content needs, not how important the dialog is.
   *
   * `sm` (360px) for a confirmation or a single field, `md` (420px, the default)
   * for a form, `lg` (760px) for something genuinely wide like a table. Pick by
   * what is inside: a confirmation at `md` has empty space either side of one
   * sentence, which reads as an unfinished layout rather than a generous one.
   */
  size?: ModalSize;
  /** Hides the corner close button. Only for a dialog the user must answer,
      and even then Escape still works, so this is presentation rather than a
      lock. */
  hideClose?: boolean;
  /**
   * Set false for a dialog the user genuinely cannot leave: it removes the close
   * button and stops Escape and a backdrop click from closing it, so the only way
   * out is the parent setting `open` to false.
   *
   * This is the real lock that `hideClose` is not, and it is close to a dark
   * pattern: it takes the whole app away until somebody complies. Use it only
   * where being unable to proceed is the actual product rule, never to make a
   * request harder to ignore.
   */
  dismissible?: boolean;
}

const CLOSE_MS = 210; // keep in step with --ui-dur-4-out

/**
 * A dialog that interrupts.
 *
 * Built on the native `<dialog>` element opened with `showModal()`, which is
 * doing real work here and not just providing a tag. It gives, for free and
 * correctly: a focus trap, focus restored to whatever opened it, Escape to
 * dismiss, the rest of the page marked inert so a screen reader cannot wander
 * out of the dialog, and a top-layer backdrop that no z-index can land above.
 * Every one of those is a thing hand-rolled modals get subtly wrong.
 *
 * Modals are expensive: they stop what the user was doing and take the keyboard
 * away. Use one to confirm something irreversible or to ask for input that
 * genuinely blocks progress. For anything else, a panel or an inline form does
 * not interrupt.
 */
export default function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideClose = false,
  dismissible = true,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [closing, setClosing] = useState(false);
  const titleId = useId();

  const beginClose = useCallback(() => {
    if (ref.current?.open) setClosing(true);
  }, []);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setClosing(false);
      dialog.showModal();
    } else if (!open && dialog.open) {
      beginClose();
    }
  }, [open, beginClose]);

  // Let the exit animation finish before the element leaves the top layer.
  // A timeout rather than `animationend`, because reduced motion collapses the
  // animation to 0.01ms and the event is not reliably delivered at that length.
  useEffect(() => {
    if (!closing) return;
    const t = setTimeout(() => {
      setClosing(false);
      ref.current?.close();
    }, CLOSE_MS);
    return () => clearTimeout(t);
  }, [closing]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Escape fires the native `cancel` event. Intercepting it lets the exit
    // animation play, and keeps `open` the single source of truth: without this
    // the element closes itself while the prop still says it is open, and the
    // next open is ignored.
    // Escape is always prevented, and only a dismissible dialog then acts on it.
    // Preventing it is what makes `dismissible={false}` a lock rather than a hint:
    // the native default would close the element out from under the prop.
    const onCancel = (e: Event) => {
      e.preventDefault();
      if (dismissible) onClose?.();
    };
    // `close` also fires for a form submitted with method="dialog", so the
    // parent state is corrected however the dialog went away.
    const onCloseEvent = () => {
      if (open && dismissible) onClose?.();
    };
    // A click on the backdrop lands on the dialog element itself, because the
    // backdrop is a pseudo-element with no hit target of its own. Comparing the
    // target is what separates it from a click on the content inside. Bound
    // natively rather than as an onClick prop: React would see a click handler
    // on a non-interactive element and rightly want a keyboard equivalent,
    // which here is Escape and is already handled above.
    const onBackdropClick = (e: MouseEvent) => {
      if (e.target === dialog && dismissible) onClose?.();
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('close', onCloseEvent);
    dialog.addEventListener('click', onBackdropClick);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('close', onCloseEvent);
      dialog.removeEventListener('click', onBackdropClick);
    };
  }, [open, onClose, dismissible]);

  return (
    <dialog
      ref={ref}
      className={[styles.dialog, styles[size], closing ? styles.closing : styles.opening]
        .filter(Boolean)
        .join(' ')}
      aria-labelledby={titleId}
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          {hideClose || !dismissible || !onClose ? null : (
            <IconButton icon="close" label="Close dialog" size="sm" onClick={onClose} />
          )}
        </div>
        {children ? <div className={styles.body}>{children}</div> : null}
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </dialog>
  );
}
